import { SparklesIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

interface BuilderStartProps {
  /** Omitted when the concierge is unavailable on this environment. */
  onDescribe?: () => void;
  onManual: () => void;
  /**
   * Logged-out visitor arriving from the guided demo.
   *
   * The two routes stop being peers here, because only one of them can
   * actually finish: building by hand ends in a save that needs an account,
   * so offering it as an equal choice sends people down a path that stops at
   * a wall. The manual card is disabled and says why, and the assisted route
   * is marked as the way on.
   */
  demoPush?: boolean;
}

/**
 * The two ways to start a watchlist, as peers.
 *
 * Identical treatment on purpose: same border, same background, same button.
 * The assisted route used to be a tinted panel with a live input inside it
 * while the manual route was a plain card with a button, which made them read
 * as a feature and its fallback rather than two ways of doing one thing. Both
 * are now a description and a button, and both open the same full-width
 * container to work in — only what you do once you are in there differs.
 *
 * Buttons rather than live fields: a field here looked symmetrical but behaved
 * badly, swapping the page out from under you mid-keystroke. Pressing a button
 * to open a form is a change you asked for.
 */
export default function BuilderStart({
  onDescribe,
  onManual,
  demoPush = false,
}: BuilderStartProps) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
      <div className="w-full px-5 py-5">
        <div
          className={clsx(
            "grid items-stretch gap-4",
            onDescribe ? "lg:grid-cols-2" : "max-w-xl"
          )}
        >
          {onDescribe && (
            <StartOption
              accent
              pulse={demoPush}
              icon={<SparklesIcon className="size-4 shrink-0 text-squawk-blue" />}
              title="Build it for me"
              body="Say what you want to follow in a sentence and we'll assemble it — checking the Newsquawk library first, so you don't end up with a worse copy of a list our desk already maintains."
              action="Build it for me"
              onClick={onDescribe}
              // The guided demo's watchlist step ends here.
              analyticsId="watchlist-build-assisted"
            />
          )}

          <StartOption
            icon={<PencilSquareIcon className="size-4 shrink-0 text-gray-400" />}
            title="Build it yourself"
            body={
              demoPush
                ? "Add tickers, companies and topics yourself, or start from one of the Newsquawk lists. Saving a watchlist needs an account, so this route is read-only in the demo."
                : "Add tickers, companies and topics yourself, or start from one of the Newsquawk lists. You can also combine several watchlists and terms together, matching on all of them or any of them."
            }
            action={demoPush ? "Needs an account" : "Build it myself"}
            onClick={onManual}
            disabled={demoPush}
            analyticsId="watchlist-build-manual"
          />
        </div>

      </div>
    </div>
  );
}

/**
 * `accent` marks the assisted route.
 *
 * The two are structurally identical — same layout, same copy block, same
 * button on the same baseline — so the colour is the only thing separating
 * them. That is deliberate: it says "this one is different in kind" without
 * making it a different shape, which is what stopped them reading as peers
 * before. The glow deepens on hover rather than being a static decoration.
 */
function StartOption({
  icon,
  title,
  body,
  action,
  onClick,
  accent = false,
  pulse = false,
  disabled = false,
  analyticsId,
}: {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  action: string;
  onClick: () => void;
  accent?: boolean;
  /** Guided demo: this is the one thing we are asking them to click. */
  pulse?: boolean;
  disabled?: boolean;
  analyticsId?: string;
}) {
  return (
    <section
      className={clsx(
        "group flex flex-col rounded-md border p-5 transition-shadow duration-300",
        accent
          ? "border-squawk-blue/40 bg-squawk-blue/[0.04] shadow-[0_0_28px_-10px_rgba(0,174,239,0.45)] hover:shadow-[0_0_36px_-8px_rgba(0,174,239,0.7)]"
          : "border-gray-700 bg-gray-900",
        pulse && "ring-2 ring-squawk-blue",
        disabled && "opacity-45"
      )}
    >
      <div className="flex items-center gap-2">
        {icon}
        <h2
          className={clsx(
            "text-xs font-semibold uppercase tracking-[0.1em]",
            accent ? "text-squawk-blue" : "text-gray-300"
          )}
        >
          {title}
        </h2>
      </div>
      <p className="mt-2 max-w-prose text-sm leading-relaxed text-gray-400">{body}</p>
      {/* Pinned to the bottom so both buttons share a baseline however much
          copy sits above them. */}
      <div className="mt-auto pt-6">
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          data-analytics-id={analyticsId}
          className={clsx(
            "w-full rounded border px-4 py-2.5 text-sm font-semibold transition-colors",
            disabled ? "cursor-not-allowed" : "cursor-pointer",
            pulse
              ? "nq-demo-pulse border-squawk-blue"
              : accent
                ? "border-squawk-blue/60 bg-squawk-blue/15 text-squawk-blue hover:border-squawk-blue hover:bg-squawk-blue/25"
                : "border-gray-600 bg-gray-800 text-gray-100 enabled:hover:border-gray-500 enabled:hover:bg-gray-700"
          )}
        >
          {action}
        </button>
      </div>
    </section>
  );
}
